import * as THREE from "three";
import {ModelsLoader} from "@view/ModelsLoader";

export class ViewPawn {
    model;
    mesh;

    capMaterial;
    innerEdgeMaterial;
    outerEdgeMaterial;
    bottomMaterial;

    isAnimating;
    animStartTime;
    animDuration;
    startPos;
    targetPos;
    targetLayer;
    shouldJump;

    constructor(scene, color, model) {
        this.model = model;

        this.isAnimating = false;
        this.animStartTime = 0;
        this.animDuration = 800;
        this.startPos = new THREE.Vector3();
        this.targetPos = new THREE.Vector3();
        this.targetLayer = 1;
        this.shouldJump = false;

        this.capMaterial = new THREE.MeshPhysicalMaterial({
            color: color,
            transparent: true,
            premultipliedAlpha: true,
            blending: THREE.MultiplyBlending,
            clearcoat: 1,
            reflectivity: 1,
            metalness: 0.5,
            roughness: 0.5,
        });
        this.outerEdgeMaterial = new THREE.MeshPhysicalMaterial({
            color: color,
            reflectivity: 1,
            metalness: 0.5,
            roughness: 0.3,
        });
        this.innerEdgeMaterial = new THREE.MeshPhysicalMaterial({
            color: color,
            reflectivity: 1,
            metalness: 0.5,
            roughness: 0.3,
        });
        this.bottomMaterial = new THREE.MeshPhysicalMaterial({
            color: color,
            reflectivity: 1,
            metalness: 0.5,
            roughness: 0.3,
        });

        if (model.player === 1) {
            this.mesh = ModelsLoader.getInstance().getModelP1();
        } else {
            this.mesh = ModelsLoader.getInstance().getModelP2();
            const scale = Math.sqrt(Math.PI) / 2;
            this.mesh.scale.set(scale, 1, scale);
        }

        this.mesh.traverse((child) => {
            if (child.isMesh) {
                //TODO: changer nom des mesh de modèles dans blender
                if (child.name === "Capuchon") {
                    child.material = this.capMaterial;
                }
                if (child.name === "Contour_Exterieur") {
                    child.material = this.outerEdgeMaterial;
                }
                if (child.name === "Contour_Interieur") {
                    child.material = this.innerEdgeMaterial;
                }
                if (child.name === "Bas") {
                    child.material = this.bottomMaterial;
                }
            }
        });

        this.mesh.renderOrder = 0;

        scene.add(this.mesh);
    }

    getColor() {
        return this.capMaterial.color;
    }

    getPlayer() {
        return this.model.player;
    }

    getX() {
        return this.model.x;
    }

    getY() {
        return this.model.y;
    }

    isEaten() {
        return this.model.eaten;
    }

    setColor(threeColor) {
        this.capMaterial.color.set(threeColor);
        this.outerEdgeMaterial.color.set(threeColor);
        this.innerEdgeMaterial.color.set(threeColor);
        this.bottomMaterial.color.set(threeColor);
    }

    setInnerEdgeColor(threeColor) {
        this.innerEdgeMaterial.color.set(threeColor);
    }

    getInnerEdgeColor() {
        return this.innerEdgeMaterial.color;
    }

    getMeshGeometry() {
        return this.model.player;
    }

    getMeshY() {
        return this.mesh.position.y;
    }

    getMeshX() {
        return this.mesh.position.x;
    }

    getMeshZ() {
        return this.mesh.position.z;
    }

    setMeshCoordinates(x, y, z) {
        this.mesh.position.set(x, y, z);
    }

    setMeshRenderOrder(order) {
        this.mesh.renderOrder = order;
    }

    moveWithAnimation(targetX, targetY, targetZ, targetLayer, shouldJump = false) {
        this.isAnimating = true;
        this.animStartTime = performance.now();
        this.shouldJump = shouldJump;

        this.startPos.copy(this.mesh.position);
        this.targetPos.set(targetX, targetY, targetZ);
        this.targetLayer = targetLayer;
    }

    updateAnimation(currentTime) {
        if (!this.isAnimating) return;

        let elapsed = currentTime - this.animStartTime;
        let progress = Math.min(elapsed / this.animDuration, 1);

        if (progress >= 1) {
            this.isAnimating = false;
            this.mesh.position.copy(this.targetPos);
            this.setMeshRenderOrder(this.targetLayer);
        } else {
            const ease = progress < 0.5
                ? 4 * progress * progress * progress
                : 1 - Math.pow(-2 * progress + 2, 3) / 2;

            this.mesh.position.x = THREE.MathUtils.lerp(this.startPos.x, this.targetPos.x, ease);
            this.mesh.position.z = THREE.MathUtils.lerp(this.startPos.z, this.targetPos.z, ease);

            const baseHeight = THREE.MathUtils.lerp(this.startPos.y, this.targetPos.y, ease);

            let jumpOffset = 0;
            if (this.shouldJump) {
                const jumpHeight = 0.3;
                jumpOffset = Math.sin(progress * Math.PI) * jumpHeight;
            }

            this.mesh.position.y = baseHeight + jumpOffset;
        }
    }

    dispose() {
        if (!this.mesh) return;

        if (this.mesh.parent) {
            this.mesh.parent.remove(this.mesh);
        }

        this.mesh.traverse((child) => {
            if (!child.isMesh) return;
            const mat = child.material;
            if (!mat) return;

            if (Array.isArray(mat)) {
                mat.forEach((m) => m && m.dispose && m.dispose());
            } else if (mat.dispose) {
                mat.dispose();
            }

            child.material = null;
        });

        this.capMaterial = null;
        this.innerEdgeMaterial = null;
        this.outerEdgeMaterial = null;
        this.bottomMaterial = null;

        this.isAnimating = false;
        this.model = null;
        this.startPos = null;
        this.targetPos = null;
        this.mesh = null;
    }
}